import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import { KeyRounded, LockRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, InputAdornment, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AuthError } from 'v2/core/services/auth/errors';
import useChangePassword from 'v2/core/services/auth/useChangePassword';
import useForgetPassword from 'v2/core/services/auth/useForgetPassword';

interface ResetPasswordWithVerificationProps {
  email: string;
}

export const ResetPasswordWithVerification = ({
  email,
}: ResetPasswordWithVerificationProps) => {
  const [OTPCode, setOTPCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const { showSuccessNotification } = useNotificationContext();

  const navigate = useNavigate();

  const [resendOTP, resendOTPStatus] = useForgetPassword({
    email,
    onSuccess: () => {
      showSuccessNotification({
        contentComponent: 'Verification code has been sent succesfully.',
        props: {},
      });
    },
    onError: (err: AuthError) => {
      setErrorMessage(err.message);
    },
  });

  const onSuccess = () => {
    showSuccessNotification({
      contentComponent: 'Password changed successfully. Please login again.',
      props: {
        timeout: NOTIFICATION_TIMEOUT_LONG_6S,
      },
    });
    navigate('/login');
  };

  const handleOnOTPCodeChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setOTPCode(event.target.value);
  };

  const handleOnPasswordInputChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setNewPassword(event.target.value);
  };

  const [changePassword, status] = useChangePassword({
    email,
    code: OTPCode,
    password: newPassword,
    onSuccess,
    onError: (err: AuthError) => {
      setErrorMessage(err.message);
    },
  });

  return (
    <>
      <Box component="form" onSubmit={changePassword}>
        {errorMessage && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}
        <Stack spacing={3}>
          <TextField
            label="Verification Code"
            placeholder="Enter verification code"
            onChange={handleOnOTPCodeChange}
            type="text"
            value={OTPCode}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyRounded color="disabled" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              pattern: '^[0-9]*$',
            }}
          />
          <TextField
            label="New Password"
            placeholder="Enter your new password"
            onChange={handleOnPasswordInputChange}
            type="password"
            value={newPassword}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockRounded color="disabled" />
                </InputAdornment>
              ),
            }}
          />
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            type="submit"
            loading={IsLoading(status)}
            sx={{ mb: 2 }}
          >
            Change Password
          </LoadingButton>
        </Stack>
      </Box>
      <Stack pt={1} direction="row" justifyContent="flex-end">
        <LoadingButton
          loading={IsLoading(resendOTPStatus)}
          onClick={resendOTP}
          size="small"
          variant="text"
        >
          Resend OTP
        </LoadingButton>
      </Stack>
    </>
  );
};
